import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChangeEventHandler, useState } from 'react';

import { useRedisWritesBlocked } from '@/apis/call';
import { updateSearch } from '@/apis/searches';
import {
  DUPLICATE_SEARCH_ERROR_MSG,
  SAVE_SEARCH_DESCRIPTION_MAX_LEN,
  SAVE_SEARCH_NAME_MAX_LEN,
} from '@/constants';
import { ApiBodyError, isDefinedError } from '@/errors';
import { Search } from '@/types';

const useEditSearch = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { enqueueSuccessNotification } = useSaladBar();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ search }: { search: Partial<Search> & Pick<Search, 'name' | 'id'> }) => {
      return updateSearch(await getAccessTokenSilently(), search.id, search);
    },
    onSuccess: ({ id }: { id: number }) => {
      enqueueSuccessNotification('The search has been saved');
      queryClient.invalidateQueries({ queryKey: ['searches'] });
      queryClient.refetchQueries({ queryKey: ['search', id] });
    },
  });
};

type EditSearchDialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  currentSearch: Search;
};

const EditSearchDialog = ({
  isOpen,
  handleClose: handleDialogClose,
  currentSearch,
}: EditSearchDialogProps) => {
  const [searchName, setSearchName] = useState(currentSearch?.name ?? '');
  const [searchDescription, setSearchDescription] = useState(currentSearch?.description ?? '');
  const { enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();
  const { mutateAsync: editSearch, isError, error } = useEditSearch();

  const getErrorText = () => {
    if (isError && error instanceof ApiBodyError) {
      if (isDefinedError(error)) {
        const { message, name } = error.body.errors[0];
        if (name === 'UpdateSearchDuplicateNameSavedBySubError') return DUPLICATE_SEARCH_ERROR_MSG;
        return message;
      }
      return 'There was an issue with the backend.';
    }
    return '';
  };

  const errorText: string = getErrorText();

  // Possible candidate for useMemo, but no performance issues noticed
  const handleEdit = async () => {
    if (writesBlocked) return;
    try {
      await editSearch({
        search: {
          id: currentSearch.id,
          name: searchName,
          description: searchDescription,
        },
      });
      handleDialogClose();
    } catch {
      enqueueErrorNotification('The search could not be saved.');
    }
  };

  const handleSearchNameChange: ChangeEventHandler<HTMLInputElement> = (e) =>
    setSearchName(e.target.value);

  const handleSearchDescriptionChange: ChangeEventHandler<HTMLInputElement> = (e) =>
    setSearchDescription(e.target.value);

  return (
    <AgDialog
      dialogTitle="Edit search"
      isOpen={isOpen}
      onClose={handleDialogClose}
      primaryButton={{
        text: 'Save',
        disabled: writesBlocked || searchName.length < 3,
        onClick: handleEdit,
      }}
    >
      <TextField
        margin="dense"
        id="name"
        label={'Search name (must be unique)'}
        type="text"
        fullWidth
        variant="standard"
        value={searchName}
        onChange={handleSearchNameChange}
        error={isError}
        helperText={errorText}
        slotProps={{
          htmlInput: {
            maxLength: SAVE_SEARCH_NAME_MAX_LEN,
          },
        }}
      />
      <TextField
        margin="dense"
        id="description"
        label={'Search description'}
        type="text"
        fullWidth
        variant="filled"
        value={searchDescription}
        onChange={handleSearchDescriptionChange}
        slotProps={{
          htmlInput: {
            maxLength: SAVE_SEARCH_DESCRIPTION_MAX_LEN,
          },
        }}
        multiline
        minRows={2}
      />
    </AgDialog>
  );
};

export default EditSearchDialog;
